<template>
    <div>
        <div class="card shadow mb-5">
            <div class="card-header">Contrôle de saisie hebdomadaire</div>

            <b-card-body>
                <div v-if="!hasErrors" class="row mb-3">
                    <div class="col-4"></div>
                    <div class="col-8 text-right">
                        <week-handler
                            :start="meta.start"
                            :end="meta.end"
                            :next="meta.next"
                            :prev="meta.prev"
                            @week-has-changed="getItems"
                        />
                    </div>
                </div>
                <div v-if="loading">
                    <loading></loading>
                </div>

                <div v-else>
                    <b-alert variant="danger" :show="hasErrors">
                        <span v-html="errorMessage"></span>
                    </b-alert>

                    <b-table
                        responsive
                        small
                        :items="items"
                        :fields="fields"
                        :filter="filter"
                        :per-page="perPage"
                        :current-page="currentPage"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                        primary-key="id"
                        @filtered="onFiltered"
                    >
                        <template #cell(email)="data">
                            <span
                                v-html="handleTimeSheetEmail(data.item.email)"
                            ></span>
                        </template>
                        <template #cell(sum.billable)="data">
                            <month-charge-handler
                                :charge="{ duration: data.item.sum.billable }"
                            />
                        </template>
                        <template #cell(sum.not_billable)="data">
                            <month-charge-handler
                                :charge="{
                                    duration: data.item.sum.not_billable,
                                }"
                            />
                        </template>
                        <template #cell(sum.absence)="data">
                            <month-charge-handler
                                :charge="{ duration: data.item.sum.absence }"
                            />
                        </template>
                        <template #cell(sum.not_affected)="data">
                            <month-charge-handler
                                :charge="{
                                    duration: data.item.sum.not_affected,
                                }"
                            />
                        </template>
                        <template #cell(sum.duration)="data">
                            <month-charge-handler
                                :charge="{ duration: data.item.sum.duration }"
                            />
                        </template>
                        <template #cell(contractual_week_hours)="data">
                            <month-charge-handler
                                :charge="{
                                    duration: data.item.contractual_week_hours,
                                }"
                            />
                        </template>
                        <template #cell(charge)="data">
                            {{ data.item.charge }}%
                        </template>
                        <template #cell(charge_variant)="data">
                            <b-progress
                                :value="data.item.charge"
                                max="100"
                                :variant="data.item.charge_variant"
                                height="12px"
                                class="mt-1"
                            />
                        </template>
                        <template slot="bottom-row" class="bg-secondary">
                            <td></td>
                            <td class="td-bold">TOTAL</td>
                            <td class="text-right td-bold">
                                <month-charge-handler :charge="totalBillable" />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :charge="totalNotBillable"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler :charge="totalAbsence" />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :charge="totalNotAffected"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler :charge="totalSum" />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :charge="totalWeekLimit"
                                />
                            </td>
                            <td class="text-right td-bold">
                                {{ totalCharge }}%
                            </td>
                            <td>
                                <b-progress
                                    :value="totalCharge"
                                    max="100"
                                    :variant="totalChargeVariant"
                                    height="12px"
                                    class="mt-1"
                                />
                            </td>
                        </template>
                    </b-table>

                    <!-- Pagination -->
                    <b-row>
                        <b-col md="3" class="my-1 text-center">
                            <b-pagination
                                v-if="totalRows > 0"
                                v-model="currentPage"
                                :total-rows="totalRows"
                                :per-page="perPage"
                                class="my-0"
                                size="sm"
                            />
                        </b-col>
                        <b-col md="6" class="my-2">
                            <p class="text-center small pt-0">
                                {{ legend }}
                            </p>
                        </b-col>
                        <b-col md="3" class="my-1 text-right">
                            <b-dropdown
                                right
                                split
                                size="sm"
                                :text="perPage + ' éléments par page'"
                            >
                                <b-dropdown-item
                                    v-for="pageOption in pageOptions"
                                    :key="pageOption"
                                    @click="setPerPage(pageOption)"
                                >
                                    {{ pageOption }}
                                </b-dropdown-item>
                            </b-dropdown>
                        </b-col>
                    </b-row>
                    <!-- /Pagination -->
                </div>
            </b-card-body>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import TableHandler from '../../../common/mixins/TableHandler.vue'
import MonthChargeHandler from '../../../common/components/MonthChargeHandler.vue'
import DashboardHandler from '../../../dashboard/mixins/DashboardHandler.vue'
import loading from '../../../common/components/loading.vue'
import weekHandler from './WeekHandler.vue'

export default {
    name: 'CollaboratorsWeekChargeControl',

    components: {
        MonthChargeHandler,
        loading,
        weekHandler,
    },

    mixins: [TableHandler, DashboardHandler],

    props: {
        todayY: {
            required: true,
            type: Number,
        },
        todayM: {
            required: true,
            type: Number,
        },
        todayD: {
            required: true,
            type: Number,
        },
    },

    data() {
        return {
            meta: {},
            fields: [
                {
                    key: 'email',
                    label: '',
                    class: 'td-icon',
                },
                {
                    key: 'name',
                    label: 'Collaborateur',
                    sortable: true,
                },
                {
                    key: 'sum.billable',
                    label: 'Facturable',
                    sortable: true,
                    class: 'text-right col-uniform',
                },
                {
                    key: 'sum.not_billable',
                    label: 'Non facturable',
                    sortable: true,
                    class: 'text-right col-uniform',
                },
                {
                    key: 'sum.absence',
                    label: 'Absence',
                    sortable: true,
                    class: 'text-right col-uniform',
                },
                {
                    key: 'sum.not_affected',
                    label: 'Non affecté',
                    sortable: true,
                    class: 'text-right col-uniform',
                },
                {
                    key: 'sum.duration',
                    label: 'Total saisi',
                    sortable: true,
                    class: 'text-right td-bold col-uniform',
                },
                {
                    key: 'contractual_week_hours',
                    label: 'Contractuel',
                    sortable: true,
                    class: 'text-right col-uniform',
                },
                {
                    key: 'charge',
                    label: 'Taux',
                    sortable: true,
                    class: 'td-bold text-right ',
                },
                {
                    key: 'charge_variant',
                    label: '',
                    class: 'col-uniform',
                },
            ],
        }
    },

    computed: {
        ...mapState({
            accountingFirmUser: (state) =>
                state.timeSheetsVM.filters.accountingFirmUser,
            currentDate: (state) => state.timeSheetsVM.currentDate,
        }),

        totalBillable() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.sum.billable),
                0
            )
            return {
                duration,
            }
        },

        totalNotBillable() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.sum.not_billable),
                0
            )
            return {
                duration,
            }
        },

        totalAbsence() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.sum.absence),
                0
            )
            return {
                duration,
            }
        },

        totalNotAffected() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.sum.not_affected),
                0
            )
            return {
                duration,
            }
        },

        totalWeekLimit() {
            const duration = this.items.reduce(
                (accum, item) =>
                    accum + parseFloat(item.contractual_week_hours),
                0
            )
            return {
                duration,
            }
        },

        totalSum() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.sum.duration),
                0
            )
            return {
                duration,
            }
        },

        totalCharge() {
            const charge =
                (100 * this.totalSum.duration) / this.totalWeekLimit.duration
            return charge.toFixed(0)
        },

        totalChargeVariant() {
            const charge =
                (100 * this.totalSum.duration) / this.totalWeekLimit.duration
            if (charge > 100) {
                return 'primary'
            }
            if (charge > this.meta.success) {
                return 'success'
            }
            if (charge > this.meta.warning) {
                return 'warning'
            }
            return 'danger'
        },
    },

    async created() {
        this.$store.commit(
            'browsing/SET_PAGE_NAME',
            'accountingFirmTimeSheetWeekChargeControl'
        )
        this.$store.commit(
            'timeSheetsVM/SET_CURRENT_DATE',
            `${this.todayY}/${this.todayM}/${this.todayD}`
        )
        this.$store.commit('reportingVM/RESET_SEARCH')
        await this.getItems()
    },

    methods: {
        async getItems() {
            try {
                this.loading = true
                const response = await this.$axios.get(
                    `/time-sheets/reporting/accountingFirms/week/${this.currentDate}`
                )
                this.items = response.data.data
                this.meta = response.data.meta
                this.loading = false
                this.updateTotalRows()
            } catch (error) {
                // console.log(error)
                this.handleErrors(error.response)
            }
        },
    },
}
</script>

<style scoped></style>
